import { useState, useCallback } from 'react'
import PropTypes from 'prop-types'

import { plural } from '@aytm/app/stats/lib/helpers'
import { buildUidFilter, PINNED_STATUS_CLAUSE } from '@aytm/app/stats/lib/uid-filter'
import {
  can,
  CAPABILITY,
  tierFromFeatures,
  exportAllowedFromFeatures
} from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// components
import { Button } from 'primereact/button'

import DataCentrifugeSection from '@aytm/app/stats/page/sidebar/data-quality/DataCentrifugeSection'

// styles
import style from '@aytm/app/stats/page/sidebar/data-quality/styles/DataQualityPanel.module.scss'

const INTRO =
  'Data quality is a multistage process built on three pillars: high-quality panelists, thoughtful survey design, and automated protection and data purification tools - all reinforced by human-in-the-loop oversight.'
const GUIDE = 'Data Quality report guides you through the entire process, ensuring you receive insights you can trust.'

const DataQualityPanel = ({ root, model, features, onExport }) => {
  const [failed, setFailed] = useState(false)
  const [processing, setProcessing] = useState(false)

  // A guest's report is view-only: the backend withholds export by clearing the feature's export flag,
  // so the panel suppresses the affordance regardless of tier. Authorized viewers carry it true.
  const canExport = exportAllowedFromFeatures(features)
  const dc = model.dataCentrifuge || {}
  // The DQR's open accordion section is published onto the DC controller's listProps
  // (sectionOpen) — the Data Centrifuge controls show only while that section is open.
  const dcSectionOpen = Boolean(dc.sectionOpen)
  const { quarantineUIDs, onFilter } = dc

  // Effective DQR tier gates the Data Centrifuge list (e.g. subvectors are hidden below Payg).
  // Prefer the controller's reconciled wire-first tier so the panel gates on the same value as
  // the report body; fall back to features for the first paint before /dqr commits.
  const tier = dc.tier || tierFromFeatures(features)

  // One quarantined figure for the panel, resolved the way the section header resolves its own: a
  // vector toggle recomputes the set, so a viewer who can toggle counts the uid set they hold, and
  // every tier below counts the producer's aggregate, which is narrowed to what that tier may see.
  // Nullish, not truthy: a payload reporting nothing quarantined must keep its zero, or the
  // fallback answers with the whole set and the guard below can never withhold the control.
  const dcQuarantineCount = can(tier, CAPABILITY.SELECT_VECTORS)
    ? quarantineUIDs?.length || 0
    : (dc.quarantineCount ?? 0)

  // The per-vector re-run is offered only to a viewer who can actually start a run — the tier
  // that grants vector selection is wider than the roles the run feature resolves on for.
  const runEnabled = !features?.stat_data_cleaning_run?.disabled

  const showFilter = 0 < dcQuarantineCount && can(tier, CAPABILITY.RELEASE_QUARANTINE)
  const showExport = 0 < dcQuarantineCount && can(tier, CAPABILITY.EXPORT_QUARANTINED) && canExport

  // A dispatched release stays quarantined until it settles — a failure puts it back — so both
  // actions would go on naming responses the reader has just let go. They stand down for the round
  // trip, the way the Release control itself does, and keep their figures while they wait.
  const releaseInFlight = 0 < (dc.restoringUIDs?.length || 0)

  // `root` is the scroll/boundary container the Data Centrifuge popups position against.
  // const [root, setRoot] = useState(null)

  // const setComponentRef = useCallback(node => setRoot(node ?? null), [])

  // Mirrors Controls#onHandleFilter, but scoped to the quarantined set rather than the
  // full flagged set, so the filter applies to exactly the responses the label counts.
  const onHandleFilter = useCallback(() => {
    if (!dcQuarantineCount) return

    // The filter is already scoped to the quarantined uids, so the status clause always applies.
    onFilter('uid.*?', buildUidFilter(quarantineUIDs, PINNED_STATUS_CLAUSE))
  }, [quarantineUIDs, dcQuarantineCount, onFilter])

  // The run polls server-side for as long as it takes, and reports back through one callback:
  // `false` when it starts, `true` — or nothing, on the download hop — once it is over either way.
  const onHandleExport = useCallback(() => {
    setFailed(false)
    setProcessing(true)

    onExport((done = true, error = false) => {
      setProcessing(!done)
      if (error) setFailed(true)
    })
  }, [onExport])

  return (
    <>
      <div data-testid="stat-data-quality-panel" className={style.component}>
        <div data-testid="stat-data-quality-panel-title" className={style['component-title']}>
          Data Quality Report
        </div>

        {!dcSectionOpen ? (
          // Intro copy is replaced by the Data Centrifuge controls once that section opens.
          <>
            <div data-testid="stat-data-quality-panel-intro" className={`${style.row} ${style.note}`}>
              {INTRO}
            </div>
            <div data-testid="stat-data-quality-panel-guide" className={`${style.row} ${style.note}`}>
              {GUIDE}
            </div>
          </>
        ) : null}

        {showFilter || showExport ? (
          // Each action carries its own gate — Filter is admin-only, Export runs the tier range below
          // it and is dropped for a view-only (guest) viewer — so the row renders whenever either
          // survives and is absent entirely when neither does.
          <div className={`${style.row} ${style['row-buttons']} p-fluid`}>
            {showFilter ? (
              <Button
                data-testid="stat-data-quality-panel-filter-quarantined"
                className="p-button-rounded p-button-bright-blue"
                label={`Filter ${dcQuarantineCount} quarantined ${plural(dcQuarantineCount, 'response', 'responses')}`}
                disabled={releaseInFlight}
                onClick={releaseInFlight ? null : onHandleFilter}
              />
            ) : null}
            {showExport ? (
              <Button
                data-testid="stat-data-quality-panel-export-quarantined"
                className="p-button-rounded p-button-bright-blue p-button-secondary"
                label={`Export ${dcQuarantineCount} quarantined ${plural(dcQuarantineCount, 'response', 'responses')}`}
                loading={processing}
                disabled={processing || releaseInFlight}
                onClick={processing || releaseInFlight ? null : onHandleExport}
              />
            ) : null}
          </div>
        ) : null}

        {failed ? (
          <div data-testid="stat-data-quality-panel-export-error" className={`${style.row} ${style['row-error']}`}>
            An error occurred while performing selected export operation. If the problem is reproducible please contact
            Support team.
          </div>
        ) : null}

        <div className={style.row}>
          <a
            data-testid="stat-data-quality-panel-guide-link"
            href="https://aytm.showpad.com/share/mcbhuFrUIzQT513cwcKbj"
            className={style['guide-link']}
            target="_blank"
          >
            Read our Data Quality Guide
          </a>
        </div>

        {dcSectionOpen ? <DataCentrifugeSection root={root} tier={tier} runEnabled={runEnabled} model={model} /> : null}
      </div>
    </>
  )
}

DataQualityPanel.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  features: PropTypes.object,
  onExport: PropTypes.func
}
DataQualityPanel.displayName = 'DataQualityPanel'
export default DataQualityPanel
